/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office, __CM_ENV__ */

// Configuration du serveur MCP : URL distincte par environnement.
const MCP_SERVER_URLS = {
  prod: "https://gesdo.cm.prod.aco.pro",
  uat: "https://gesdo.cm.uat.aco.pro",
  dev: "https://gesdo.cm.uat.aco.pro",
};
const MCP_SERVER_URL = MCP_SERVER_URLS[detectEnvironment()] || MCP_SERVER_URLS.prod;
const MCP_API_KEY = "vxP6AgnSmF4jtmY21J7eWLXC6+jYYfj2hy5V+w+2bCI=";

// Configuration SharePoint
const SHAREPOINT_DELIVERY = {
  type: "sharepoint",
  sharePointSiteUrl: "https://alcyoneconsultinglux.sharepoint.com/sites/CMDeveloppement",
  sharePointLibrary: "Entrees",
  sharePointFolder: "",
};

let currentAttachments = [];
const MAGIC = {
  PDF:  ["25 50 44 46 2D"],
  OLE:  ["D0 CF 11 E0 A1 B1 1A E1"],
  ZIP:  ["50 4B 03 04", "50 4B 05 06", "50 4B 07 08"],
  PNG:  ["89 50 4E 47"],
  JPEG: ["FF D8 FF"],
  GIF:  ["47 49 46 38"],
  BMP:  ["42 4D"],
  TIFF: ["49 49 2A 00", "4D 4D 00 2A"],
  WEBP: ["52 49 46 46"]
};

const ALLOWED_EXT = new Set([
  "pdf", "doc", "docx", "xls", "xlsx",
  "png", "jpg", "jpeg", "gif", "bmp", "tiff", "tif", "webp"
]);
const BLOCKED_EXT = new Set(["docm", "dotm", "xlsm"]);
const ALLOWED_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png", "image/jpeg", "image/gif", "image/bmp", "image/tiff", "image/webp"
]);

// Bandeau d'environnement : rendu indépendamment d'Office.onReady pour
// rester visible y compris lors d'une preview navigateur hors Outlook.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", renderEnvBanner);
} else {
  renderEnvBanner();
}

Office.onReady((info) => {
  const t = Office.context?.officeTheme;
  if (t?.bodyBackgroundColor) {
    document.documentElement.style.setProperty("--cm-bg", t.bodyBackgroundColor);
  }
  if (t?.bodyForegroundColor) {
    document.documentElement.style.setProperty("--cm-text", t.bodyForegroundColor);
  }

  renderEnvBanner();

  if (info.host === Office.HostType.Outlook) {
    document.getElementById("loadAttachments").onclick = loadAttachmentList;
    document.getElementById("sendSelected").onclick = sendSelected;
  }
});

// ── Détection de l'environnement ────────────────────────────────────────────

function detectEnvironment() {
  // Valeur injectée au build via webpack DefinePlugin (CM_ENV=uat|dev|prod).
  // Prioritaire sur la détection par hostname pour un contrôle explicite côté serveur.
  const built = typeof __CM_ENV__ !== "undefined" ? __CM_ENV__ : "";
  if (built === "uat" || built === "dev" || built === "prod") return built;

  const host = (window.location.hostname || "").toLowerCase();
  if (host === "localhost" || host === "127.0.0.1") return "dev";
  if (host.includes("uat")) return "uat";
  return "prod";
}

function renderEnvBanner() {
  const banner = document.getElementById("env-banner");
  const label = document.getElementById("env-banner-label");
  if (!banner || !label) return;

  const env = detectEnvironment();
  banner.classList.remove("cm-env-banner--uat", "cm-env-banner--dev");

  if (env === "prod") {
    banner.hidden = true;
    return;
  }

  if (env === "dev") {
    banner.classList.add("cm-env-banner--dev");
    label.textContent = "Environnement DEV";
  } else {
    banner.classList.add("cm-env-banner--uat");
    label.textContent = "Environnement UAT";
  }
  banner.hidden = false;
}

// ── Validation magic bytes ──────────────────────────────────────────────────

async function validateAttachment(att) {
  const name = (att?.name || "").trim();
  const lower = name.toLowerCase();
  const ext = lower.includes(".") ? lower.split(".").pop() : "";

  if (BLOCKED_EXT.has(ext)) {
    return { ok: false, reason: `Extension macro interdite: .${ext}` };
  }

  if (!ALLOWED_EXT.has(ext)) {
    return { ok: false, reason: `Extension non autorisée: .${ext || "?"}` };
  }

  if (att.contentType &&
      att.contentType !== "application/octet-stream" &&
      !ALLOWED_TYPES.has(att.contentType)) {
    return { ok: false, reason: `MIME non autorisé: ${att.contentType}` };
  }

  const head = await getAttachmentHeadBytes(att.id, 8);
  const sig2 = toHex(head.slice(0, 2));
  const sig3 = toHex(head.slice(0, 3));
  const sig4 = toHex(head.slice(0, 4));
  const sig5 = toHex(head.slice(0, 5));
  const sig8 = toHex(head.slice(0, 8));

  // Documents
  if (ext === "pdf") {
    if (!MAGIC.PDF.includes(sig5)) {
      return { ok: false, reason: "Signature PDF invalide (%PDF- manquant)" };
    }
    return { ok: true };
  }

  if (ext === "doc" || ext === "xls") {
    if (!MAGIC.OLE.includes(sig8)) {
      return { ok: false, reason: `Signature ${ext.toUpperCase()} (OLE) invalide` };
    }
    return { ok: true };
  }

  if (ext === "docx" || ext === "xlsx") {
    if (!MAGIC.ZIP.includes(sig4)) {
      return { ok: false, reason: `Signature ${ext.toUpperCase()} invalide (ZIP attendu)` };
    }
    return { ok: true };
  }

  // Images
  if (ext === "png") {
    if (!MAGIC.PNG.includes(sig4)) {
      return { ok: false, reason: "Signature PNG invalide" };
    }
    return { ok: true };
  }

  if (ext === "jpg" || ext === "jpeg") {
    if (!MAGIC.JPEG.includes(sig3)) {
      return { ok: false, reason: "Signature JPEG invalide" };
    }
    return { ok: true };
  }

  if (ext === "gif") {
    if (!MAGIC.GIF.includes(sig4)) {
      return { ok: false, reason: "Signature GIF invalide" };
    }
    return { ok: true };
  }

  if (ext === "bmp") {
    if (!MAGIC.BMP.includes(sig2)) {
      return { ok: false, reason: "Signature BMP invalide" };
    }
    return { ok: true };
  }

  if (ext === "tiff" || ext === "tif") {
    if (!MAGIC.TIFF.includes(sig4)) {
      return { ok: false, reason: "Signature TIFF invalide" };
    }
    return { ok: true };
  }

  if (ext === "webp") {
    if (!MAGIC.WEBP.includes(sig4)) {
      return { ok: false, reason: "Signature WEBP invalide" };
    }
    return { ok: true };
  }

  return { ok: false, reason: "Type non supporté" };
}

function getAttachmentHeadBytes(attachmentId, nBytes = 8) {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.getAttachmentContentAsync(attachmentId, async (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(result.error);
        return;
      }

      const { content, format } = result.value;

      if (format === Office.MailboxEnums.AttachmentContentFormat.Base64 || format === "base64") {
        resolve(base64HeadToUint8Array(content, nBytes));
        return;
      }

      if (format === Office.MailboxEnums.AttachmentContentFormat.Url || format === "url") {
        try {
          const head = await fetchHeadBytes(content, nBytes);
          resolve(head);
        } catch (e) {
          reject(e);
        }
        return;
      }

      reject(new Error("Format de pièce jointe non supporté: " + format));
    });
  });
}

function base64HeadToUint8Array(base64, nBytes) {
  const neededChars = Math.ceil(nBytes / 3) * 4;
  const chunk = base64.slice(0, neededChars);

  const binary = atob(chunk);
  const bytes = new Uint8Array(Math.min(binary.length, nBytes));
  for (let i = 0; i < bytes.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function fetchHeadBytes(url, nBytes) {
  const res = await fetch(url, { headers: { Range: `bytes=0-${nBytes - 1}` } });
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf).slice(0, nBytes);
}

function toHex(uint8) {
  return Array.from(uint8)
    .map(b => b.toString(16).padStart(2, "0").toUpperCase())
    .join(" ");
}

// ── Lecture contenu pièce jointe ────────────────────────────────────────────

function getAttachmentContentAsync(attachmentId) {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.getAttachmentContentAsync(attachmentId, async (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message || "Impossible de lire la pièce jointe"));
        return;
      }

      const { content, format } = result.value;

      if (format === Office.MailboxEnums.AttachmentContentFormat.Base64 || format === "base64") {
        resolve(content);
        return;
      }

      // Office.js retourne le format "url" pour les pièces jointes cloud (OneDrive/SharePoint)
      // et parfois les fichiers volumineux. Il faut alors télécharger les octets et les
      // ré-encoder en base64 pour respecter le contrat JSON attendu par le MCP.
      if (format === Office.MailboxEnums.AttachmentContentFormat.Url || format === "url") {
        try {
          const bytes = await fetchAllBytes(content);
          resolve(uint8ArrayToBase64(bytes));
        } catch (e) {
          reject(e);
        }
        return;
      }

      reject(new Error("Format de pièce jointe non supporté: " + format));
    });
  });
}

async function fetchAllBytes(url) {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Téléchargement de la pièce jointe échoué (HTTP ${res.status})`);
  }
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
}

function getMimeType(fileName) {
  const name = (fileName || "").toLowerCase();
  if (name.endsWith(".pdf"))  return "application/pdf";
  if (name.endsWith(".doc"))  return "application/msword";
  if (name.endsWith(".docx")) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (name.endsWith(".xls"))  return "application/vnd.ms-excel";
  if (name.endsWith(".xlsx")) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (name.endsWith(".png"))  return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  if (name.endsWith(".gif"))  return "image/gif";
  if (name.endsWith(".bmp"))  return "image/bmp";
  if (name.endsWith(".tiff") || name.endsWith(".tif")) return "image/tiff";
  if (name.endsWith(".webp")) return "image/webp";
  return "application/octet-stream";
}

// ── Chargement des pièces jointes ───────────────────────────────────────────

function loadAttachmentList() {
  const status = el("status");
  const list = el("attachment-list");
  const sendBtn = el("sendSelected");

  status.innerHTML = spinner("Chargement des pièces jointes...");
  list.innerHTML = "";
  sendBtn.style.display = "none";
  currentAttachments = [];

  const item = Office.context.mailbox.item;

  if (!item?.attachments?.length) {
    status.innerHTML = errorMsg("Aucune pièce jointe");
    return;
  }

  const docs = item.attachments.filter(isAllowedAttachment);

  if (!docs.length) {
    status.innerHTML = errorMsg("Aucun fichier compatible trouvé");
    return;
  }

  currentAttachments = docs;

  status.innerHTML = infoMsg(`${docs.length} fichier(s) trouvé(s). Sélectionnez ceux à envoyer.`);
  sendBtn.style.display = "flex";

  list.innerHTML = `
      <div class="cm-list-head">
        <div class="cm-list-title">Fichiers</div>
        <div class="cm-list-actions">
          <a href="#" id="checkAll" class="cm-link">Tout cocher</a>
          <a href="#" id="uncheckAll" class="cm-link">Tout décocher</a>
        </div>
      </div>
      <div id="items" class="cm-card"></div>
    `;

  const items = document.getElementById("items");

  docs.forEach((att, idx) => {
    const name = escapeHtml(att.name || `fichier-${idx + 1}`);
    const type = escapeHtml(friendlyType(att));

    items.insertAdjacentHTML("beforeend", `
      <label class="cm-item">
        <input type="checkbox" class="att-check" data-id="${att.id}" checked />
        <div style="flex:1; min-width:0;">
          <div class="cm-item-name">${name}</div>
          <div class="cm-item-type">${type}</div>
        </div>
        <span class="cm-badge">${type}</span>
      </label>
    `);
  });

  document.getElementById("checkAll").onclick = (e) => { e.preventDefault(); setAll(true); };
  document.getElementById("uncheckAll").onclick = (e) => { e.preventDefault(); setAll(false); };
}

// ── Envoi et conversion ─────────────────────────────────────────────────────

async function sendSelected() {
  const status = el("status");
  const sendBtn = el("sendSelected");
  const selected = getSelectedAttachments();

  if (!selected.length) {
    status.innerHTML = errorMsg("Aucun fichier sélectionné");
    return;
  }

  sendBtn.disabled = true;

  // Étape 1 : Validation des signatures (magic bytes)
  status.innerHTML = spinner("Vérification des signatures...");

  for (const att of selected) {
    let res;
    try {
      res = await validateAttachment(att);
    } catch (e) {
      console.error(e);
      status.innerHTML = errorMsg(`Erreur lecture pièce jointe: ${att.name}`);
      sendBtn.disabled = false;
      return;
    }

    if (!res.ok) {
      status.innerHTML = errorMsg(`Rejeté: ${att.name} -- ${res.reason}`);
      sendBtn.disabled = false;
      return;
    }
  }

  // Étape 2 : Lecture du contenu des pièces jointes
  try {
    const files = [];

    // Étape 2a : Génération du document Word des informations du mail
    status.innerHTML = spinner("Génération du document mail...");
    const emailDoc = await generateEmailDocx();
    files.push(emailDoc);

    // Étape 2b : Lecture des pièces jointes sélectionnées
    for (const att of selected) {
      status.innerHTML = spinner(`Lecture de ${escapeHtml(att.name)}...`);
      const contentBase64 = await getAttachmentContentAsync(att.id);

      files.push({
        fileName: att.name,
        mimeType: att.contentType || getMimeType(att.name),
        contentBase64
      });
    }

    // Étape 3 : Envoi vers SharePoint via le serveur MCP
    // Le MCP convertit chaque fichier en PDF puis les fusionne :
    // la sortie est UN PDF, donc le sharePointFileName doit finir en .pdf
    status.innerHTML = spinner("Envoi vers SharePoint...");

    const subject = Office.context.mailbox.item.subject || "Email";
    const uniqueId = generateUniqueId();
    const delivery = {
      ...SHAREPOINT_DELIVERY,
      sharePointFileName: sanitizeFileName(subject) + "_" + uniqueId + ".pdf",
    };

    const response = await fetch(`${MCP_SERVER_URL}/api/convert`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": MCP_API_KEY
      },
      body: JSON.stringify({ files, delivery })
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || `Erreur serveur (${response.status})`);
    }

    // Étape 4 : Affichage du résultat avec lien direct
    const fileName = delivery.sharePointFileName;
    const folder = delivery.sharePointFolder ? delivery.sharePointFolder + "/" : "";
    const directUrl = `${delivery.sharePointSiteUrl}/${delivery.sharePointLibrary}/${folder}${encodeURIComponent(fileName)}`;

    status.innerHTML = successMsg(`Envoi reussi ! ${files.length} fichier(s) envoye(s) vers SharePoint.`);
    status.insertAdjacentHTML("beforeend", `
      <div style="margin-top: 10px;">
        <a href="${escapeHtml(directUrl)}" target="_blank" class="cm-link" style="font-weight: 600;">
          <svg class="cm-icon" width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M12.5 2a.5.5 0 0 0 0 1h3.8l-7.2 7.2a.5.5 0 1 0 .7.7L17 3.7v3.8a.5.5 0 0 0 1 0v-5a.5.5 0 0 0-.5-.5h-5zM4 4.5A1.5 1.5 0 0 1 5.5 3H9a.5.5 0 0 1 0 1H5.5a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5V11a.5.5 0 1 1 1 0v2.5A1.5 1.5 0 0 1 14.5 15h-9A1.5 1.5 0 0 1 4 13.5v-9z"/></svg> Ouvrir sur SharePoint
        </a>
      </div>
    `);

  } catch (error) {
    console.error("Erreur lors de l'envoi:", error);
    status.innerHTML = errorMsg(`Erreur: ${error.message}`);
  } finally {
    sendBtn.disabled = false;
  }
}

// ── Génération du document Word des informations du mail ───────────────────

async function generateEmailDocx() {
  const item = Office.context.mailbox.item;

  const subject = item.subject || "(sans objet)";
  const from = formatAddress(item.from);
  const to = (item.to || []).map(formatAddress).filter(Boolean).join("; ");
  const cc = (item.cc || []).map(formatAddress).filter(Boolean).join("; ");
  const date = item.dateTimeCreated
    ? new Date(item.dateTimeCreated).toLocaleString("fr-FR")
    : "";

  let body = "";
  try {
    // On lit le corps en HTML afin de pouvoir retirer la signature insérée par
    // Outlook (conteneur <div id="Signature"> / "x_Signature") avant conversion en texte.
    const html = await getItemBodyAsync("html");
    body = htmlBodyToText(html);
  } catch (e) {
    console.warn("Lecture HTML impossible, repli sur le texte brut:", e);
    try {
      body = await getItemBodyAsync("text");
    } catch (e2) {
      console.warn("Impossible de lire le corps du mail:", e2);
    }
  }

  const docxBytes = buildDocx({ subject, from, to, cc, date, body });
  const base64 = uint8ArrayToBase64(docxBytes);
  const fileName = sanitizeFileName(subject) + ".docx";

  return {
    fileName,
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    contentBase64: base64
  };
}

function formatAddress(addr) {
  if (!addr) return "";
  const name = (addr.displayName || "").trim();
  const email = (addr.emailAddress || "").trim();
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} <${email}>`;
  }
  return email || name;
}

function getItemBodyAsync(format = "text") {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.body.getAsync(format, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value || "");
      } else {
        reject(new Error(result.error?.message || "Lecture du corps impossible"));
      }
    });
  });
}

// Convertit le corps HTML d'un mail en texte brut, en retirant au passage la
// signature insérée par Outlook. Sur OWA / nouveau Outlook, et même sur les
// mails reçus, cette signature est enveloppée dans un <div id="Signature"> (ou
// "x_Signature" après transit serveur) : on supprime ce conteneur du DOM.
function htmlBodyToText(html) {
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");

  // Suppression ciblée de la signature (id ou classe contenant « signature »,
  // insensible à la casse, ce qui couvre Signature / x_Signature).
  doc
    .querySelectorAll('[id*="signature" i], [class*="signature" i]')
    .forEach((el) => el.remove());

  // Éléments non textuels qui pollueraient le rendu.
  doc.querySelectorAll("style, script").forEach((el) => el.remove());

  return domNodeToText(doc.body)
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Parcourt récursivement un nœud DOM et reconstruit un texte brut en
// préservant les sauts de ligne des éléments de bloc et des <br>.
function domNodeToText(root) {
  if (!root) return "";
  const blockTags = new Set([
    "DIV", "P", "LI", "TR", "H1", "H2", "H3", "H4", "H5", "H6",
    "BLOCKQUOTE", "UL", "OL", "TABLE", "SECTION", "ARTICLE", "HEADER", "FOOTER"
  ]);
  let out = "";

  function walk(node) {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3 /* TEXT_NODE */) {
        // En HTML, les espaces (y compris les retours du source) se réduisent à un seul espace.
        out += String(child.nodeValue).replace(/\s+/g, " ");
        return;
      }
      if (child.nodeType !== 1 /* ELEMENT_NODE */) return;

      const tag = child.tagName;
      if (tag === "BR") {
        out += "\n";
        return;
      }
      const isBlock = blockTags.has(tag);
      if (isBlock && out && !out.endsWith("\n")) out += "\n";
      walk(child);
      if (isBlock && out && !out.endsWith("\n")) out += "\n";
    });
  }

  walk(root);
  return out;
}

function sanitizeFileName(name) {
  // eslint-disable-next-line no-control-regex
  const cleaned = String(name).replace(/[\\/:*?"<>|\x00-\x1F]/g, "_").trim();
  return (cleaned.slice(0, 100) || "Email");
}

function generateUniqueId() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const rand = Math.random().toString(36).slice(2, 6);
  return (
    d.getFullYear() +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    "_" +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    pad(d.getSeconds()) +
    "_" +
    rand
  );
}

function buildDocx({ subject, from, to, cc, date, body }) {
  const documentXml = buildDocumentXml({ subject, from, to, cc, date, body });
  const nowIso = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

  const contentTypes =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>` +
    `<Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>` +
    `<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>` +
    `<Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>` +
    `</Types>`;

  const rels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>` +
    `<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>` +
    `<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>` +
    `</Relationships>`;

  const documentRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    `</Relationships>`;

  const stylesXml =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">` +
    `<w:docDefaults>` +
    `<w:rPrDefault><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri" w:cs="Calibri"/><w:sz w:val="22"/><w:szCs w:val="22"/><w:lang w:val="fr-FR"/></w:rPr></w:rPrDefault>` +
    `<w:pPrDefault><w:pPr><w:spacing w:after="160" w:line="259" w:lineRule="auto"/></w:pPr></w:pPrDefault>` +
    `</w:docDefaults>` +
    `<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/></w:style>` +
    `</w:styles>`;

  const coreXml =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">` +
    `<dc:title>${escapeXml(subject)}</dc:title>` +
    `<dc:creator>CollegeMedical Outlook Add-in</dc:creator>` +
    `<cp:lastModifiedBy>CollegeMedical Outlook Add-in</cp:lastModifiedBy>` +
    `<dcterms:created xsi:type="dcterms:W3CDTF">${nowIso}</dcterms:created>` +
    `<dcterms:modified xsi:type="dcterms:W3CDTF">${nowIso}</dcterms:modified>` +
    `</cp:coreProperties>`;

  const appXml =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">` +
    `<Application>CollegeMedical Outlook Add-in</Application>` +
    `</Properties>`;

  return zipStore([
    { name: "[Content_Types].xml", data: contentTypes },
    { name: "_rels/.rels", data: rels },
    { name: "docProps/app.xml", data: appXml },
    { name: "docProps/core.xml", data: coreXml },
    { name: "word/_rels/document.xml.rels", data: documentRels },
    { name: "word/document.xml", data: documentXml },
    { name: "word/styles.xml", data: stylesXml },
  ]);
}

function buildDocumentXml({ subject, from, to, cc, date, body }) {
  const parts = [];
  parts.push(titlePara(subject));
  parts.push(fieldPara("De", from));
  parts.push(fieldPara("À", to));
  if (cc) parts.push(fieldPara("Cc", cc));
  parts.push(fieldPara("Date", date));
  parts.push(fieldPara("Objet", subject));
  parts.push(separatorPara());

  const lines = String(body || "").split(/\r?\n/);
  for (const line of lines) parts.push(textPara(line));

  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">` +
    `<w:body>` +
    parts.join("") +
    `<w:sectPr>` +
    `<w:pgSz w:w="11906" w:h="16838"/>` +
    `<w:pgMar w:top="1134" w:right="1134" w:bottom="1134" w:left="1134" w:header="708" w:footer="708" w:gutter="0"/>` +
    `</w:sectPr>` +
    `</w:body>` +
    `</w:document>`
  );
}

function titlePara(text) {
  return (
    `<w:p>` +
    `<w:pPr><w:spacing w:after="240"/></w:pPr>` +
    `<w:r><w:rPr><w:b/><w:sz w:val="32"/></w:rPr>` +
    `<w:t xml:space="preserve">${escapeXml(text)}</w:t>` +
    `</w:r></w:p>`
  );
}

function fieldPara(label, value) {
  return (
    `<w:p>` +
    `<w:r><w:rPr><w:b/></w:rPr>` +
    `<w:t xml:space="preserve">${escapeXml(label)} : </w:t></w:r>` +
    `<w:r><w:t xml:space="preserve">${escapeXml(value)}</w:t></w:r>` +
    `</w:p>`
  );
}

function separatorPara() {
  return (
    `<w:p>` +
    `<w:pPr><w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="999999"/></w:pBdr><w:spacing w:after="240"/></w:pPr>` +
    `</w:p>`
  );
}

function textPara(text) {
  if (!text) return `<w:p/>`;
  return `<w:p><w:r><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r></w:p>`;
}

function escapeXml(s) {
  return String(s ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// ── Mini-encodeur ZIP (mode STORE, sans compression) ────────────────────────

function zipStore(files) {
  const encoder = new TextEncoder();
  const entries = files.map(f => {
    const nameBytes = encoder.encode(f.name);
    const dataBytes = typeof f.data === "string" ? encoder.encode(f.data) : f.data;
    return { nameBytes, dataBytes, crc: crc32(dataBytes) };
  });

  let localSize = 0;
  let centralSize = 0;
  for (const e of entries) {
    localSize += 30 + e.nameBytes.length + e.dataBytes.length;
    centralSize += 46 + e.nameBytes.length;
  }
  const total = localSize + centralSize + 22;
  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  let offset = 0;
  const localOffsets = [];

  for (const e of entries) {
    localOffsets.push(offset);
    view.setUint32(offset, 0x04034b50, true); offset += 4;
    view.setUint16(offset, 20, true); offset += 2;       // version needed
    view.setUint16(offset, 0, true); offset += 2;        // flags
    view.setUint16(offset, 0, true); offset += 2;        // compression (stored)
    view.setUint16(offset, 0, true); offset += 2;        // mod time
    view.setUint16(offset, 0x21, true); offset += 2;     // mod date (1980-01-01)
    view.setUint32(offset, e.crc, true); offset += 4;
    view.setUint32(offset, e.dataBytes.length, true); offset += 4;
    view.setUint32(offset, e.dataBytes.length, true); offset += 4;
    view.setUint16(offset, e.nameBytes.length, true); offset += 2;
    view.setUint16(offset, 0, true); offset += 2;        // extra length
    out.set(e.nameBytes, offset); offset += e.nameBytes.length;
    out.set(e.dataBytes, offset); offset += e.dataBytes.length;
  }

  const centralStart = offset;

  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    view.setUint32(offset, 0x02014b50, true); offset += 4;
    view.setUint16(offset, 20, true); offset += 2;       // version made by
    view.setUint16(offset, 20, true); offset += 2;       // version needed
    view.setUint16(offset, 0, true); offset += 2;        // flags
    view.setUint16(offset, 0, true); offset += 2;        // compression
    view.setUint16(offset, 0, true); offset += 2;        // mod time
    view.setUint16(offset, 0x21, true); offset += 2;     // mod date
    view.setUint32(offset, e.crc, true); offset += 4;
    view.setUint32(offset, e.dataBytes.length, true); offset += 4;
    view.setUint32(offset, e.dataBytes.length, true); offset += 4;
    view.setUint16(offset, e.nameBytes.length, true); offset += 2;
    view.setUint16(offset, 0, true); offset += 2;        // extra length
    view.setUint16(offset, 0, true); offset += 2;        // comment length
    view.setUint16(offset, 0, true); offset += 2;        // disk number
    view.setUint16(offset, 0, true); offset += 2;        // internal attrs
    view.setUint32(offset, 0, true); offset += 4;        // external attrs
    view.setUint32(offset, localOffsets[i], true); offset += 4;
    out.set(e.nameBytes, offset); offset += e.nameBytes.length;
  }

  view.setUint32(offset, 0x06054b50, true); offset += 4;
  view.setUint16(offset, 0, true); offset += 2;
  view.setUint16(offset, 0, true); offset += 2;
  view.setUint16(offset, entries.length, true); offset += 2;
  view.setUint16(offset, entries.length, true); offset += 2;
  view.setUint32(offset, centralSize, true); offset += 4;
  view.setUint32(offset, centralStart, true); offset += 4;
  view.setUint16(offset, 0, true); offset += 2;

  return out;
}

let CRC_TABLE = null;
function crc32(uint8) {
  if (!CRC_TABLE) {
    CRC_TABLE = new Uint32Array(256);
    for (let i = 0; i < 256; i++) {
      let c = i;
      for (let k = 0; k < 8; k++) {
        c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
      }
      CRC_TABLE[i] = c >>> 0;
    }
  }
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < uint8.length; i++) {
    crc = CRC_TABLE[(crc ^ uint8[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

function uint8ArrayToBase64(uint8) {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < uint8.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, uint8.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

// ── Utilitaires ─────────────────────────────────────────────────────────────

function setAll(checked) {
  document.querySelectorAll(".att-check").forEach(cb => cb.checked = checked);
}

function getSelectedAttachments() {
  const selectedIds = Array.from(document.querySelectorAll(".att-check"))
    .filter(cb => cb.checked)
    .map(cb => cb.getAttribute("data-id"));

  return currentAttachments.filter(att => selectedIds.includes(att.id));
}

function isAllowedAttachment(att) {
  if (!att) return false;

  // Exclure les images inline (logos de signature, images intégrées au corps du mail).
  // Office.js les expose comme des pièces jointes, mais leur contenu retourné par
  // getAttachmentContentAsync n'est souvent pas exploitable par Chromium côté MCP.
  if (att.isInline) return false;

  if (att.contentType && ALLOWED_TYPES.has(att.contentType)) return true;

  const name = (att.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  return ALLOWED_EXT.has(ext);
}

function friendlyType(att) {
  const name = (att?.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";

  const labels = {
    pdf: "PDF", doc: "DOC", docx: "DOCX",
    xls: "XLS", xlsx: "XLSX",
    png: "PNG", jpg: "JPG", jpeg: "JPEG",
    gif: "GIF", bmp: "BMP",
    tiff: "TIFF", tif: "TIF", webp: "WEBP"
  };

  return labels[ext] || att?.contentType || "Fichier";
}

function el(id) { return document.getElementById(id); }

function escapeHtml(str) {
  return String(str)
    .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;").replaceAll("'", "&#039;");
}

function spinner(text) {
  return `<span class="cm-status-info"><svg class="cm-icon cm-icon-spin" width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M10 3a7 7 0 0 1 6.7 5l.8-1.1a.5.5 0 0 1 .8.6l-1.6 2.3a.5.5 0 0 1-.7.1l-2.3-1.6a.5.5 0 0 1 .6-.8l1.2.8A6 6 0 1 0 10 16a6 6 0 0 0 4.8-2.4.5.5 0 1 1 .8.6A7 7 0 1 1 10 3z"/></svg> ${escapeHtml(text)}</span>`;
}
function errorMsg(text) {
  return `<span class="cm-status-error">${escapeHtml(text)}</span>`;
}
function successMsg(text) {
  return `<span class="cm-status-success">${escapeHtml(text)}</span>`;
}
function infoMsg(text) {
  return `<span class="cm-status-info">${escapeHtml(text)}</span>`;
}
